// eslint-disable-next-line no-restricted-imports -- Direct HTTP calls needed for Redux session hydration outside React
import request from "superagent";

import { apiClient } from "../../api/apiClient";
import { getAuthHeader } from "../../../utils/superagent";
import { APP_V2_APP_EVENTS, logError, logEvent } from "../../lib/analytics";
import { showErrorToast } from "../../lib/Notifications";
import { isNetworkFailure, LOGIN_OUTCOME, LOGIN_REASON } from "../loginRum";
import { SSO_CALLBACK_PATH } from "../RipplingSso/paths";
import { fetchAuthSessionWithLock } from "./cognitoSessionLock";
import { shouldHydrateAsEmployee } from "./shouldHydrateAsEmployee";
import {
  ADMIN_PROFILE_SUCCESS,
  LOGIN_REQUESTED,
  LOGIN_SUCCESS,
  LOGOUT,
  SESSION_LOGIN_SUCCESS,
  SESSION_TOKEN_READY,
} from "./sessionActionTypes";

const HCF_USER_EVENTS = {
  LOGGED_IN: "HCF Logged In",
} as const;

const consumeOverrideUserIdUrlParameter = (): string | null => {
  const searchParameters = new URLSearchParams(window.location.search);
  const userId = searchParameters.get("overrideUserId");
  if (userId) {
    searchParameters.delete("overrideUserId");
    history.replaceState(
      window.history.state,
      "",
      `${window.location.origin}${window.location.pathname}${searchParameters.toString() ? `?${searchParameters.toString()}` : ""}`
    );
    return userId;
  }

  const requestedUrlParameter = searchParameters.get("requestedUrl");
  if (requestedUrlParameter) {
    try {
      const requestedUrl = new URL(requestedUrlParameter, window.location.origin);
      const nestedParameters = new URLSearchParams(requestedUrl.search);
      const nestedUserId = nestedParameters.get("overrideUserId");

      if (nestedUserId) {
        return nestedUserId;
      }
    } catch {
      return null;
    }
  }

  return null;
};

/**
 * Fetches the user record and dispatches LOGIN_SUCCESS.
 *
 * Focused replacement for the legacy `get()` thunk from
 * `src/modules/session.ts`, without the moment-timezone dependency chain.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function fetchSession(dispatch: any, userId: string) {
  localStorage.setItem("selectedUser", userId);
  dispatch({ type: LOGIN_REQUESTED });

  try {
    const response = await request
      .get(`${(globalThis as Record<string, unknown>).api}/user/get/${userId}`)
      .set(await getAuthHeader());

    dispatch({
      type: LOGIN_SUCCESS,
      data: { ...response.body },
    });
  } catch (error: unknown) {
    showErrorToast(error instanceof Error ? error.message : "Failed to fetch user session");
  }
}

interface FacilityUserRecord {
  _id: string;
  name: string;
  email: string;
  phone: string;
  tmz: string;
  roles: string[];
  permissions: string[];
  notify: unknown;
  isEmailVerified: boolean;
  isSuspicious: boolean;
  intercomHash?: string;
  facilities: Array<{ userId: string }>;
}

// Uses `apiClient` (not superagent) so a transiently rejected ID token
// can be force-refreshed and replayed by the response interceptor instead
// of cascading into a session-init LOGOUT.
async function getFacilityUser(): Promise<{ user: FacilityUserRecord }> {
  const response = await apiClient.get<{ user: FacilityUserRecord }>("/facilityUser/findByEmail");
  return response.data;
}

/**
 * Initializes the user session from Cognito tokens.
 *
 * Checks for an existing valid Cognito session via Amplify and hydrates
 * the Redux session.
 *
 * Returns `true` if a valid Cognito session was found and restored,
 * `false` if no Cognito session exists.
 */
// Deduplicates concurrent calls to initCognitoSession. When multiple callers
// (e.g. SSO callback + AppLayout's useEffect) dispatch initCognitoSession at
// the same time, only the first call runs the actual session hydration. All
// subsequent calls return the same in-flight promise. This prevents the
// SESSION_TOKEN_READY: { ready: false } dispatch from firing multiple times,
// which would cause render loops and duplicate API calls.
let inFlightSessionInit: Promise<boolean> | undefined;

/**
 * Resolves the analytics event name and `source` tag for a session-init
 * failure, based on where the session is being hydrated (SSO callback vs
 * passkey vs password login).
 */
function resolveSessionFailure(options?: { authMethod?: "passkey" }): {
  eventName: (typeof APP_V2_APP_EVENTS)[keyof typeof APP_V2_APP_EVENTS];
  source: string;
} {
  const isSsoCallback = globalThis.location?.pathname === SSO_CALLBACK_PATH;
  const isPasskey = options?.authMethod === "passkey";

  if (isSsoCallback) {
    return {
      eventName: APP_V2_APP_EVENTS.RIPPLING_SSO_SESSION_HYDRATION_FAILURE,
      source: "sso-callback",
    };
  }
  if (isPasskey) {
    return {
      eventName: APP_V2_APP_EVENTS.PASSKEY_LOGIN_COGNITO_SESSION_FAILURE,
      source: "passkey-session-init",
    };
  }
  return {
    eventName: APP_V2_APP_EVENTS.PASSWORD_LOGIN_COGNITO_SESSION_FAILURE,
    source: "cognito-session-init",
  };
}

export const initCognitoSession =
  (options?: { authMethod?: "passkey" }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (dispatch: any): Promise<boolean> => {
    if (inFlightSessionInit) {
      return inFlightSessionInit;
    }

    inFlightSessionInit = (async () => {
      dispatch({
        type: SESSION_TOKEN_READY,
        data: { ready: false },
      });

      try {
        const session = await fetchAuthSessionWithLock();
        const idToken = session.tokens?.idToken;

        if (!idToken) {
          dispatch({ type: LOGOUT });
          dispatch({ type: SESSION_TOKEN_READY, data: { ready: true } });
          return false;
        }

        const email = idToken.payload?.email;
        if (typeof email !== "string") {
          dispatch({ type: LOGOUT });
          dispatch({ type: SESSION_TOKEN_READY, data: { ready: true } });
          return false;
        }

        dispatch({
          type: SESSION_TOKEN_READY,
          data: { ready: true },
        });

        // Route based on `custom:user_types` from the Cognito ID token,
        // not on email pattern. _3p vendor-bypass accounts are
        // EMPLOYEE-typed and must hit handleEmployeeLogin; the
        // aliased-employee-as-facility testing convention is preserved
        // inside shouldHydrateAsEmployee.
        const userTypesAttribute = idToken.payload["custom:user_types"];
        await (shouldHydrateAsEmployee(email, userTypesAttribute)
          ? handleEmployeeLogin(dispatch)
          : handleFacilityLogin(dispatch));

        return true;
      } catch (error: unknown) {
        const { eventName, source } = resolveSessionFailure(options);
        logError(eventName, {
          error,
          metadata: {
            source,
            outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
            reason: isNetworkFailure(error)
              ? LOGIN_REASON.NETWORK_FAILURE
              : LOGIN_REASON.SESSION_HYDRATION_FAILED,
          },
        });
        dispatch({ type: LOGOUT });
        dispatch({ type: SESSION_TOKEN_READY, data: { ready: true } });
        return false;
      } finally {
        inFlightSessionInit = undefined;
      }
    })();

    return inFlightSessionInit;
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleEmployeeLogin(dispatch: any): Promise<void> {
  const response = await request
    .get(`${(globalThis as Record<string, unknown>).api}/user/getByEmail`)
    .set(await getAuthHeader());

  dispatch({ type: ADMIN_PROFILE_SUCCESS, data: { user: response.body } });

  const user = { ...response.body };

  const overrideUserId = consumeOverrideUserIdUrlParameter();
  if (overrideUserId) {
    localStorage.setItem("selectedUser", overrideUserId);
  }

  const selectedUser = localStorage.getItem("selectedUser");

  // SESSION_LOGIN_SUCCESS must be dispatched BEFORE fetchSession because the
  // LOGIN_SUCCESS reducer (in session.ts) reads state.user._id for analytics.
  // state.user is set by SESSION_LOGIN_SUCCESS.
  dispatch({
    type: SESSION_LOGIN_SUCCESS,
    data: {
      user: {
        access: user.roles,
        _id: user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        ...(user?.intercomHash && { intercomHash: user.intercomHash }),
      },
    },
  });

  await fetchSession(dispatch, selectedUser || user._id);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleFacilityLogin(dispatch: any): Promise<void> {
  const { user } = await getFacilityUser();
  if (!user?.facilities?.length) {
    throw new Error("Facility user has no associated facilities");
  }
  const facilityIdsArray = user.facilities.map((facility) => facility.userId);

  const overrideUserId = consumeOverrideUserIdUrlParameter();
  if (overrideUserId) {
    localStorage.setItem("selectedFacility", overrideUserId);
  }

  const selectedFacility = localStorage.getItem("selectedFacility");
  const selectedUser = localStorage.getItem("selectedUser");
  const isSelectedFacilityExists =
    selectedFacility !== null && facilityIdsArray.includes(selectedFacility);
  const userId: string = isSelectedFacilityExists
    ? selectedFacility
    : (facilityIdsArray[0] ?? selectedUser ?? "");
  if (!userId) {
    throw new Error("Unable to determine session user id for facility user");
  }

  if (!isSelectedFacilityExists) {
    localStorage.setItem("selectedFacility", facilityIdsArray[0]);
    logEvent(HCF_USER_EVENTS.LOGGED_IN, {
      workplaceId: userId,
      workplaceUserId: user?._id,
    });
  }

  // SESSION_LOGIN_SUCCESS must be dispatched BEFORE fetchSession because the
  // LOGIN_SUCCESS reducer (in session.ts) reads state.user._id for analytics.
  // state.user is set by SESSION_LOGIN_SUCCESS.
  dispatch({
    type: SESSION_LOGIN_SUCCESS,
    data: {
      user: {
        facilityIds: facilityIdsArray,
        tmz: user.tmz,
        access: user.roles,
        permissions: user.permissions,
        _id: user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        notify: user.notify,
        isEmailVerified: user.isEmailVerified,
        isSuspicious: user.isSuspicious,
        ...(user?.intercomHash && { intercomHash: user.intercomHash }),
      },
    },
  });

  await fetchSession(dispatch, userId);
}
